import React, { useEffect, useState } from 'react';

interface SkillRatings {
  pace?: number;
  shooting?: number;
  passing?: number;
  dribbling?: number;
  defending?: number;
  physical?: number;
}

interface SkillsRadarChartProps {
  skills: SkillRatings;
  size?: number;
  tier?: string;
}

const SkillsRadarChart: React.FC<SkillsRadarChartProps> = ({ 
  skills, 
  size = 200,
  tier = 'Gold'
}) => {
  const [animatedSkills, setAnimatedSkills] = useState<SkillRatings>({
    pace: 0,
    shooting: 0,
    passing: 0,
    dribbling: 0,
    defending: 0,
    physical: 0
  });

  const skillLabels = [
    { key: 'pace', label: 'PAC', angle: -90 },
    { key: 'shooting', label: 'SHO', angle: -30 },
    { key: 'passing', label: 'PAS', angle: 30 },
    { key: 'dribbling', label: 'DRI', angle: 90 },
    { key: 'defending', label: 'DEF', angle: 150 },
    { key: 'physical', label: 'PHY', angle: 210 }
  ];

  const getTierColors = () => {
    switch (tier) {
      case 'Epic': return { 
        stroke: '#a855f7', 
        fill: 'rgba(168, 85, 247, 0.35)', 
        glow: '#ec4899',
        secondary: '#22d3ee',
        gradient: 'url(#epicGradient)'
      };
      case 'Gold': return { 
        stroke: '#fbbf24', 
        fill: 'rgba(251, 191, 36, 0.35)', 
        glow: '#f59e0b',
        secondary: '#f97316',
        gradient: 'url(#goldGradient)'
      };
      case 'Silver': return { 
        stroke: '#94a3b8', 
        fill: 'rgba(148, 163, 184, 0.35)', 
        glow: '#64748b',
        secondary: '#cbd5e1',
        gradient: 'url(#silverGradient)'
      };
      default: return { 
        stroke: '#f97316', 
        fill: 'rgba(249, 115, 22, 0.35)', 
        glow: '#ea580c',
        secondary: '#fb923c',
        gradient: 'url(#bronzeGradient)'
      };
    }
  };

  const colors = getTierColors();
  const center = size / 2;
  const maxRadius = (size / 2) - 35;

  useEffect(() => {
    const timer = setTimeout(() => {
      setAnimatedSkills({
        pace: skills.pace || 50,
        shooting: skills.shooting || 50,
        passing: skills.passing || 50,
        dribbling: skills.dribbling || 50,
        defending: skills.defending || 50,
        physical: skills.physical || 50
      });
    }, 100);
    return () => clearTimeout(timer);
  }, [skills]);

  const getPoint = (angle: number, value: number) => {
    const normalizedValue = Math.min(99, Math.max(0, value)) / 99;
    const radius = normalizedValue * maxRadius;
    const radian = (angle * Math.PI) / 180;
    return {
      x: center + radius * Math.cos(radian),
      y: center + radius * Math.sin(radian)
    };
  };

  const createHexagonPath = (scale: number = 1) => {
    const points = skillLabels.map(skill => {
      const radian = (skill.angle * Math.PI) / 180;
      const radius = maxRadius * scale;
      return `${center + radius * Math.cos(radian)},${center + radius * Math.sin(radian)}`;
    });
    return `M${points.join('L')}Z`;
  };

  const createSkillPath = () => {
    const points = skillLabels.map(skill => {
      const value = animatedSkills[skill.key as keyof SkillRatings] || 0;
      const point = getPoint(skill.angle, value);
      return `${point.x},${point.y}`;
    });
    return `M${points.join('L')}Z`;
  };

  return (
    <div className="flex flex-col items-center premium-radar-chart">
      <svg width={size} height={size} className="overflow-visible">
        <defs>
          {/* Enhanced glow filter */}
          <filter id="premiumGlow" x="-100%" y="-100%" width="300%" height="300%">
            <feGaussianBlur stdDeviation="4" result="blur1"/>
            <feGaussianBlur stdDeviation="8" result="blur2"/>
            <feMerge>
              <feMergeNode in="blur2"/>
              <feMergeNode in="blur1"/>
              <feMergeNode in="SourceGraphic"/>
            </feMerge>
          </filter>
          
          {/* Point glow filter */}
          <filter id="pointGlow" x="-200%" y="-200%" width="500%" height="500%">
            <feGaussianBlur stdDeviation="3" result="coloredBlur"/>
            <feMerge>
              <feMergeNode in="coloredBlur"/>
              <feMergeNode in="coloredBlur"/>
              <feMergeNode in="SourceGraphic"/>
            </feMerge>
          </filter>

          {/* Tier gradients */}
          <linearGradient id="epicGradient" x1="0%" y1="0%" x2="100%" y2="100%">
            <stop offset="0%" stopColor="#a855f7" stopOpacity="0.6"/>
            <stop offset="50%" stopColor="#ec4899" stopOpacity="0.4"/>
            <stop offset="100%" stopColor="#22d3ee" stopOpacity="0.6"/>
          </linearGradient>
          <linearGradient id="goldGradient" x1="0%" y1="0%" x2="100%" y2="100%">
            <stop offset="0%" stopColor="#fbbf24" stopOpacity="0.6"/>
            <stop offset="50%" stopColor="#f59e0b" stopOpacity="0.4"/>
            <stop offset="100%" stopColor="#f97316" stopOpacity="0.6"/>
          </linearGradient>
          <linearGradient id="silverGradient" x1="0%" y1="0%" x2="100%" y2="100%">
            <stop offset="0%" stopColor="#94a3b8" stopOpacity="0.5"/>
            <stop offset="100%" stopColor="#64748b" stopOpacity="0.5"/>
          </linearGradient>
          <linearGradient id="bronzeGradient" x1="0%" y1="0%" x2="100%" y2="100%">
            <stop offset="0%" stopColor="#f97316" stopOpacity="0.6"/>
            <stop offset="100%" stopColor="#ea580c" stopOpacity="0.6"/>
          </linearGradient>
        </defs>

        {/* Background hexagons with subtle glow */}
        {[0.25, 0.5, 0.75, 1].map((scale, i) => (
          <path
            key={i}
            d={createHexagonPath(scale)}
            fill="none"
            stroke="currentColor"
            strokeWidth={i === 3 ? "1.5" : "0.5"}
            className="text-border/40"
            strokeDasharray={i < 3 ? "4 4" : "none"}
          />
        ))}

        {/* Axis lines with gradient */}
        {skillLabels.map((skill, i) => {
          const point = getPoint(skill.angle, 99);
          return (
            <line
              key={i}
              x1={center}
              y1={center}
              x2={point.x}
              y2={point.y}
              stroke="currentColor"
              strokeWidth="0.75"
              className="text-border/30"
              strokeDasharray="2 6"
            />
          );
        })}

        {/* Center point glow */}
        <circle
          cx={center}
          cy={center}
          r="4"
          fill={colors.stroke}
          filter="url(#pointGlow)"
          opacity="0.5"
        />

        {/* Main skill polygon with gradient fill */}
        <path
          d={createSkillPath()}
          fill={colors.gradient}
          stroke={colors.stroke}
          strokeWidth="2.5"
          filter="url(#premiumGlow)"
          className="transition-all duration-1000 ease-out"
        />

        {/* Secondary stroke for depth */}
        <path
          d={createSkillPath()}
          fill="none"
          stroke={colors.glow}
          strokeWidth="1"
          opacity="0.5"
          className="transition-all duration-1000 ease-out"
        />

        {/* Premium skill points with pulsing glow */}
        {skillLabels.map((skill, i) => {
          const value = animatedSkills[skill.key as keyof SkillRatings] || 0;
          const point = getPoint(skill.angle, value);
          return (
            <g key={i}>
              {/* Outer glow ring */}
              <circle
                cx={point.x}
                cy={point.y}
                r="8"
                fill={colors.stroke}
                opacity="0.2"
                className="transition-all duration-1000 ease-out premium-point-pulse"
              />
              {/* Main point */}
              <circle
                cx={point.x}
                cy={point.y}
                r="5"
                fill={colors.stroke}
                className="transition-all duration-1000 ease-out"
                filter="url(#pointGlow)"
              />
              {/* Inner highlight */}
              <circle
                cx={point.x}
                cy={point.y}
                r="2"
                fill="white"
                opacity="0.8"
                className="transition-all duration-1000 ease-out"
              />
            </g>
          );
        })}

        {/* Labels with enhanced styling */}
        {skillLabels.map((skill, i) => {
          const labelPoint = getPoint(skill.angle, 125);
          const value = animatedSkills[skill.key as keyof SkillRatings] || 0;
          return (
            <g key={i}>
              {/* Label background */}
              <rect
                x={labelPoint.x - 16}
                y={labelPoint.y - 18}
                width="32"
                height="28"
                rx="6"
                fill="hsl(var(--background))"
                fillOpacity="0.8"
                stroke={colors.stroke}
                strokeWidth="1"
                strokeOpacity="0.3"
              />
              <text
                x={labelPoint.x}
                y={labelPoint.y - 6}
                textAnchor="middle"
                className="text-[9px] font-bold fill-muted-foreground"
              >
                {skill.label}
              </text>
              <text
                x={labelPoint.x}
                y={labelPoint.y + 8}
                textAnchor="middle"
                className="text-[12px] font-black"
                fill={colors.stroke}
              >
                {Math.round(value)}
              </text>
            </g>
          );
        })}
      </svg>
    </div>
  );
};

export default SkillsRadarChart;
